import { BaseObj } from '@/objects/BaseObj';
import { useBaseObjEntityStateHandler } from '@/objects/entitystatehandler/entitystatehandler';
import { isRef, ref, unref } from 'vue';
import { Participant } from './Participant';
import type { EntityState } from './../enums/EntityState';
import type { LicenceIDType } from './../enums/LicenceIDType';
import { formatDateIso } from '@/utilities/dateutils';

export class LicenceSearchItem extends BaseObj {
    public ClubName: string | null;
    public __clubname: string | null;

    public ExpiryDate: Date | null;
    public __expirydate: Date | null;

    public FederationID: number | null;
    public __federationid: number | null;

    public readonly Id: string | null;

    public IsPaid: boolean | null;
    public __ispaid: boolean | null;

    public LicenceName: string | null;
    public __licencename: string | null;

    public LicencePeriod: string | null;
    public __licenceperiod: string | null;

    public LicenceStatus: string | null;
    public __licencestatus: string | null;

    public LicenceType: LicenceIDType;
    public __licencetype: LicenceIDType;

    public LicenceValue: string;
    public __licencevalue: string;

    public NIFBuypassID: number | null;
    public __nifbuypassid: number | null;

    public NIFPersonID: number | null;
    public __nifpersonid: number | null;

    public PaidDate: Date | null;
    public __paiddate: Date | null;

    public readonly Participant: Participant | null;

    public ParticipantName: string | null;
    public __participantname: string | null;

    public StartDate: Date | null;
    public __startdate: Date | null;

    public __entityStateHandler;
    public __selfEntityState: EntityState;
    public __entityState: any;
    public get EntityState(): EntityState {
        return unref(this.__entityState);
    }
    /***
     * @deprecated Use the static FromJsValues method instead of the constructor to get type safety and default values
     */
    public constructor(values: {
        ClubName?: string | null;
        EntityState?: EntityState;
        ExpiryDate?: Date | null;
        FederationID?: number | null;
        Id?: string | null;
        IsPaid?: boolean | null;
        LicenceName?: string | null;
        LicencePeriod?: string | null;
        LicenceStatus?: string | null;
        LicenceType?: LicenceIDType;
        LicenceValue?: string | null;
        NIFBuypassID?: number | null;
        NIFPersonID?: number | null;
        PaidDate?: Date | null;
        Participant?: Participant | null;
        ParticipantName?: string | null;
        StartDate?: Date | null;
    }) {
        super();
        this.__entityState = ref(values.EntityState ?? 'Unchanged');
        this.__selfEntityState = values.EntityState ?? 'Unchanged';
        this.__entityStateHandler = useBaseObjEntityStateHandler<LicenceSearchItem>(unref(this.__entityState), (states) => {
            if (isRef(this.__entityState)) {
                this.__entityState.value = states.EntityStateWithChildrenConsidered;
            } else {
                console.warn('LicenceSearchItem tried to set entitystate, but entitystate is not a ref! This should never happen');
            }
            this.__selfEntityState = states.SelfEntityState;
        });
        this.ClubName = values.ClubName ?? null;
        this.__clubname = values.ClubName ?? null;
        Object.defineProperty(this, 'ClubName', {
            get(): string | null {
                return this.__clubname;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('ClubName', this.__clubname, value);
                this.__clubname = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.ExpiryDate = values.ExpiryDate ?? null;
        this.__expirydate = values.ExpiryDate ?? null;
        Object.defineProperty(this, 'ExpiryDate', {
            get(): Date | null {
                return this.__expirydate;
            },
            set(value: Date | null) {
                this.__entityStateHandler.propertyChanged('ExpiryDate', this.__expirydate, value);
                this.__expirydate = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.FederationID = values.FederationID ?? null;
        this.__federationid = values.FederationID ?? null;
        Object.defineProperty(this, 'FederationID', {
            get(): number | null {
                return this.__federationid;
            },
            set(value: number | null) {
                this.__entityStateHandler.propertyChanged('FederationID', this.__federationid, value);
                this.__federationid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Id = values.Id ?? '';

        this.IsPaid = values.IsPaid ?? null;
        this.__ispaid = values.IsPaid ?? null;
        Object.defineProperty(this, 'IsPaid', {
            get(): boolean | null {
                return this.__ispaid;
            },
            set(value: boolean | null) {
                this.__entityStateHandler.propertyChanged('IsPaid', this.__ispaid, value);
                this.__ispaid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.LicenceName = values.LicenceName ?? null;
        this.__licencename = values.LicenceName ?? null;
        Object.defineProperty(this, 'LicenceName', {
            get(): string | null {
                return this.__licencename;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('LicenceName', this.__licencename, value);
                this.__licencename = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.LicencePeriod = values.LicencePeriod ?? null;
        this.__licenceperiod = values.LicencePeriod ?? null;
        Object.defineProperty(this, 'LicencePeriod', {
            get(): string | null {
                return this.__licenceperiod;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('LicencePeriod', this.__licenceperiod, value);
                this.__licenceperiod = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.LicenceStatus = values.LicenceStatus ?? null;
        this.__licencestatus = values.LicenceStatus ?? null;
        Object.defineProperty(this, 'LicenceStatus', {
            get(): string | null {
                return this.__licencestatus;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('LicenceStatus', this.__licencestatus, value);
                this.__licencestatus = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.LicenceType = values.LicenceType ?? 'BUYPASS';
        this.__licencetype = values.LicenceType ?? 'BUYPASS';
        Object.defineProperty(this, 'LicenceType', {
            get(): LicenceIDType {
                return this.__licencetype;
            },
            set(value: LicenceIDType) {
                this.__entityStateHandler.propertyChanged('LicenceType', this.__licencetype, value);
                this.__licencetype = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.LicenceValue = values.LicenceValue ?? '';
        this.__licencevalue = values.LicenceValue ?? '';
        Object.defineProperty(this, 'LicenceValue', {
            get(): string {
                return this.__licencevalue;
            },
            set(value: string) {
                this.__entityStateHandler.propertyChanged('LicenceValue', this.__licencevalue, value);
                this.__licencevalue = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.NIFBuypassID = values.NIFBuypassID ?? null;
        this.__nifbuypassid = values.NIFBuypassID ?? null;
        Object.defineProperty(this, 'NIFBuypassID', {
            get(): number | null {
                return this.__nifbuypassid;
            },
            set(value: number | null) {
                this.__entityStateHandler.propertyChanged('NIFBuypassID', this.__nifbuypassid, value);
                this.__nifbuypassid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.NIFPersonID = values.NIFPersonID ?? null;
        this.__nifpersonid = values.NIFPersonID ?? null;
        Object.defineProperty(this, 'NIFPersonID', {
            get(): number | null {
                return this.__nifpersonid;
            },
            set(value: number | null) {
                this.__entityStateHandler.propertyChanged('NIFPersonID', this.__nifpersonid, value);
                this.__nifpersonid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.PaidDate = values.PaidDate ?? null;
        this.__paiddate = values.PaidDate ?? null;
        Object.defineProperty(this, 'PaidDate', {
            get(): Date | null {
                return this.__paiddate;
            },
            set(value: Date | null) {
                this.__entityStateHandler.propertyChanged('PaidDate', this.__paiddate, value);
                this.__paiddate = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Participant = values.Participant ?? null;
        this.Participant = values.Participant ?? null;
        this.Participant?._setNotifyParentOfModificationCallback(() =>
            this.__entityStateHandler.childBaseObjChanged('Participant', this.Participant),
        );

        this.ParticipantName = values.ParticipantName ?? null;
        this.__participantname = values.ParticipantName ?? null;
        Object.defineProperty(this, 'ParticipantName', {
            get(): string | null {
                return this.__participantname;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('ParticipantName', this.__participantname, value);
                this.__participantname = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.StartDate = values.StartDate ?? null;
        this.__startdate = values.StartDate ?? null;
        Object.defineProperty(this, 'StartDate', {
            get(): Date | null {
                return this.__startdate;
            },
            set(value: Date | null) {
                this.__entityStateHandler.propertyChanged('StartDate', this.__startdate, value);
                this.__startdate = value;
            },
            enumerable: true,
            configurable: true,
        });
    }

    public _setNotifyParentOfModificationCallback(callback: () => void) {
        this.__entityStateHandler.setNotifyParentOfModificationCallback(callback);
    }

    public serialize(): string {
        return (
            '{' +
            `"ClubName": ${JSON.stringify(this.ClubName)}` +
            ',' +
            `"EntityState": ${unref(this.__selfEntityState) == 'Unchanged' ? 0 : unref(this.__selfEntityState) == 'Deleted' ? 1 : unref(this.__selfEntityState) == 'Modified' ? 2 : unref(this.__selfEntityState) == 'Added' ? 3 : 0}` +
            ',' +
            `"ExpiryDate": ${this.ExpiryDate ? '"' + formatDateIso(this.ExpiryDate) + '"' : null}` +
            ',' +
            `"FederationID": ${JSON.stringify(this.FederationID)}` +
            ',' +
            `"Id": ${JSON.stringify(this.Id)}` +
            ',' +
            `"IsPaid": ${JSON.stringify(this.IsPaid)}` +
            ',' +
            `"LicenceName": ${JSON.stringify(this.LicenceName)}` +
            ',' +
            `"LicencePeriod": ${JSON.stringify(this.LicencePeriod)}` +
            ',' +
            `"LicenceStatus": ${JSON.stringify(this.LicenceStatus)}` +
            ',' +
            `"LicenceType": ${this.LicenceType == 'BUYPASS' ? 0 : this.LicenceType == 'ISONEN' ? 1 : 0}` +
            ',' +
            `"LicenceValue": ${JSON.stringify(this.LicenceValue)}` +
            ',' +
            `"NIFBuypassID": ${JSON.stringify(this.NIFBuypassID)}` +
            ',' +
            `"NIFPersonID": ${JSON.stringify(this.NIFPersonID)}` +
            ',' +
            `"PaidDate": ${this.PaidDate ? '"' + formatDateIso(this.PaidDate) + '"' : null}` +
            ',' +
            `"Participant": ${!this.Participant ? null : this.Participant?.serialize()}` +
            ',' +
            `"ParticipantName": ${JSON.stringify(this.ParticipantName)}` +
            ',' +
            `"StartDate": ${this.StartDate ? '"' + formatDateIso(this.StartDate) + '"' : null}` +
            ',' +
            `"ChangedColumns": ${unref(this.__selfEntityState) !== 'Modified' ? 'null' : JSON.stringify(this.__entityStateHandler.getChangedPropertiesArray())}` +
            '}'
        );
    }

    /**
     * Sets the entity state to Deleted
     */
    public Delete() {
        if (this.__selfEntityState === 'Added') {
            throw new Error('Cannot delete an object that is in Added state. It has not been saved yet.');
        }
        this.__selfEntityState = 'Deleted';
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Undeletes the object by setting resetting the EntityState
     *
     * If the object has modifications, the new state will be 'Modified', otherwise 'Unchanged'
     */
    public DeleteUndo() {
        this.__selfEntityState = 'Unchanged';
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
                     * Sets the entity state to Added
                     *
                     
                     * 
                     * Note: This does not do anything with the children, so use with caution
                     * 
                     * The use case of this is to make copies of existing objects as new objects without having to fill every field.
                     */
    public SetEntityStateAsAddedAndResetId() {
        if (this.EntityState !== 'Unchanged') {
            throw new Error('Changing a modified object to Added is not supported.');
        }
        this.__selfEntityState = 'Added';

        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Creates an instance of LicenceSearchItem from the supplied values.
     * Missing values have default values
     * @param values The values you want to set
     * @returns An object instance with the supplied values set
     */
    public static FromJsValues(values: {
        ClubName?: string | null;
        ExpiryDate?: Date | null;
        FederationID?: number | null;
        IsPaid?: boolean | null;
        LicenceName?: string | null;
        LicencePeriod?: string | null;
        LicenceStatus?: string | null;
        LicenceType?: LicenceIDType;
        LicenceValue?: string | null;
        NIFBuypassID?: number | null;
        NIFPersonID?: number | null;
        PaidDate?: Date | null;
        Participant?: Participant | null;
        ParticipantName?: string | null;
        StartDate?: Date | null;
        EntityState?: 'Added' | 'Unchanged';
    }): LicenceSearchItem {
        return new LicenceSearchItem(values);
    }

    public static _FromServerJson(json: any): LicenceSearchItem {
        return new LicenceSearchItem({
            ClubName: json.ClubName ?? '',
            ExpiryDate: json.ExpiryDate ? (json.ExpiryDate instanceof Date ? json.ExpiryDate : new Date(json.ExpiryDate)) : null,
            FederationID: json.FederationID ?? null,
            Id: json.Id ?? '',
            IsPaid: json.IsPaid ?? null,
            LicenceName: json.LicenceName ?? '',
            LicencePeriod: json.LicencePeriod ?? '',
            LicenceStatus: json.LicenceStatus ?? '',
            LicenceType: json.LicenceType == 0 ? 'BUYPASS' : json.LicenceType == 1 ? 'ISONEN' : 'BUYPASS',
            LicenceValue: json.LicenceValue ?? '',
            NIFBuypassID: json.NIFBuypassID ?? null,
            NIFPersonID: json.NIFPersonID ?? null,
            PaidDate: json.PaidDate ? (json.PaidDate instanceof Date ? json.PaidDate : new Date(json.PaidDate)) : null,
            Participant: json.Participant ? Participant._FromServerJson(json.Participant) : null,
            ParticipantName: json.ParticipantName ?? '',
            StartDate: json.StartDate ? (json.StartDate instanceof Date ? json.StartDate : new Date(json.StartDate)) : null,
            EntityState: 'Unchanged',
        });
    }

    /**
     * Creates an instance of LicenceSearchItem from another instance of LicenceSearchItem.
     * @param other The LicenceSearchItem you want to clone
     * @returns A cloned LicenceSearchItem
     */
    public static FromOther(other: LicenceSearchItem): LicenceSearchItem {
        if (other.EntityState !== 'Unchanged') {
            throw new Error('Cloning an object with EntityState other than Unchanged is not supported');
        }
        return LicenceSearchItem._FromServerJson(JSON.parse(other.serialize()));
    }

    /**
     * Deepclones the object.
     */
    public DeepClone() {
        return LicenceSearchItem.FromOther(this);
    }

    /**
     * IsPropertyChanged.
     */
    public IsPropertyChanged(
        ...props: (
            | 'ClubName'
            | 'ExpiryDate'
            | 'FederationID'
            | 'Id'
            | 'IsPaid'
            | 'LicenceName'
            | 'LicencePeriod'
            | 'LicenceStatus'
            | 'LicenceType'
            | 'LicenceValue'
            | 'NIFBuypassID'
            | 'NIFPersonID'
            | 'PaidDate'
            | 'Participant'
            | 'ParticipantName'
            | 'StartDate'
        )[]
    ) {
        return this.__entityStateHandler.isPropertyChanged(...props);
    }
    static NAME_OF_CLASS = 'LicenceSearchItem';
}
